import { useState, useEffect, useRef } from "react";
import { mockDataStore, mockIntegrations } from "../components/utils/mockData";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  FileText,
  TrendingUp,
  Activity,
  Zap,
  Download,
  Share,
  Calendar,
  Target,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";

import HealthScoreCard from "../components/reports/HealthScoreCard";
import BiomarkerTrends from "../components/reports/BiomarkerTrends";
import PersonalizedRecommendations from "../components/reports/PersonalizedRecommendations";
import HealthGoalsTracker from "../components/reports/HealthGoalsTracker";
import FollowUpScheduler from "../components/reports/FollowUpScheduler";

export default function Reports() {
  const [selectedPatient, setSelectedPatient] = useState("PAT001");
  const [selectedReportType, setSelectedReportType] = useState("comprehensive");
  const [timeRange, setTimeRange] = useState("6months");
  const [reportData, setReportData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [healthScore, setHealthScore] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [showFollowUpScheduler, setShowFollowUpScheduler] = useState(false);
  const [patientDetails, setPatientDetails] = useState(null);
  const goalsTrackerRef = useRef();

  useEffect(() => {
    loadPatientData();
  }, [selectedPatient, timeRange]);

  const loadPatientData = async () => {
    try {
      const [tests, notes, prescriptions, appointments, patients] =
        await Promise.all([
          mockDataStore.filter(
            "bloodTests",
            { patient_id: selectedPatient },
            "-test_date"
          ),
          mockDataStore.filter(
            "clinicalNotes",
            { patient_id: selectedPatient },
            "-visit_date"
          ),
          mockDataStore.filter(
            "prescriptions",
            { patient_id: selectedPatient },
            "-prescription_date"
          ),
          mockDataStore.filter(
            "serviceAppointments",
            { patient_id: selectedPatient },
            "-appointment_date"
          ),
          mockDataStore.filter("patients", { patient_id: selectedPatient }),
        ]);

      setReportData({ tests, notes, prescriptions, appointments });
      if (patients.length > 0) {
        setPatientDetails(patients[0]);
      }
      await generateHealthScore(tests);
      await generateRecommendations(tests, notes);
    } catch (error) {
      console.error("Error loading patient data:", error);
    }
  };

  const generateHealthScore = async (tests) => {
    if (tests.length === 0) return;

    setIsGenerating(true);
    try {
      const latestTest = tests[0];
      const testResults = latestTest.results || {};

      const prompt = `
        Based on these blood test results, calculate a comprehensive health score (0-100) and provide insights:
        Results: ${JSON.stringify(testResults)}
      `;

      const analysis = await mockIntegrations.InvokeLLM({
        prompt,
        response_json_schema: {
          type: "object",
          properties: { overall_score: { type: "number" } },
        },
      });
      setHealthScore(analysis);
    } catch (error) {
      console.error("Error generating health score:", error);
    }
    setIsGenerating(false);
  };

  const generateRecommendations = async (tests, notes) => {
    if (tests.length === 0) return;

    try {
      const latestTest = tests[0];
      const recentNotes = notes.slice(0, 3);

      const prompt = `
        Based on this patient's blood work and clinical notes, provide personalized health recommendations:
        Latest Blood Results: ${JSON.stringify(latestTest.results)}
        Recent Clinical Notes: ${recentNotes.map((note) => note.assessment).join("; ")}
      `;

      const result = await mockIntegrations.InvokeLLM({
        prompt,
        response_json_schema: {
          type: "object",
          properties: { recommendations: { type: "array" } },
        },
      });
      setRecommendations(result.recommendations || []);
    } catch (error) {
      console.error("Error generating recommendations:", error);
    }
  };

  const generateComprehensiveReport = async () => {
    if (!reportData) return;

    setIsGenerating(true);
    try {
      const chartPrompt = `Create a clean, professional health dashboard visualization showing an overall health score.`;
      const chartImage = await mockIntegrations.GenerateImage({
        prompt: chartPrompt,
      });
    } catch (error) {
      console.error("Error generating comprehensive report:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const renderReportContent = () => {
    switch (selectedReportType) {
      case "comprehensive":
        return (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-8">
              {reportData && (
                <BiomarkerTrends
                  tests={reportData.tests}
                  timeRange={timeRange}
                />
              )}

              {recommendations.length > 0 && (
                <PersonalizedRecommendations
                  recommendations={recommendations}
                />
              )}
            </div>

            {/* Right Column - 1/3 width */}
            <div className="space-y-8">
              <HealthGoalsTracker
                ref={goalsTrackerRef}
                patientId={selectedPatient}
              />

              {/* Quick Actions */}
              <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-600" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={() => setShowFollowUpScheduler(true)}
                  >
                    <Calendar className="w-4 h-4" />
                    Schedule Follow-up
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={() => goalsTrackerRef.current?.toggleAddGoalForm()}
                  >
                    <Target className="w-4 h-4" />
                    Set Health Goals
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export PDF Report
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "biomarkers":
        return (
          <div className="space-y-8">
            {reportData && (
              <BiomarkerTrends tests={reportData.tests} timeRange={timeRange} />
            )}

            {/* Additional biomarker-specific content */}
            <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-600" />
                  Biomarker Analysis Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">
                      Key Findings
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Glucose levels showing improvement
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Cholesterol trending downward
                      </li>
                      <li className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        Monitor triglyceride levels
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">
                      Next Steps
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li>• Continue current medication regimen</li>
                      <li>• Schedule follow-up in 3 months</li>
                      <li>• Consider additional cardiovascular screening</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "trends":
        return (
          <div className="space-y-8">
            {reportData && (
              <BiomarkerTrends tests={reportData.tests} timeRange={timeRange} />
            )}

            {/* Trends-specific content */}
            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Improving Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="font-medium text-green-900">
                        HDL Cholesterol
                      </span>
                      <div className="flex items-center gap-2 text-green-700">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm">+12% improvement</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="font-medium text-green-900">
                        Glucose Control
                      </span>
                      <div className="flex items-center gap-2 text-green-700">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm">+8% improvement</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    Areas Needing Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                      <span className="font-medium text-orange-900">
                        LDL Cholesterol
                      </span>
                      <div className="flex items-center gap-2 text-orange-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">Monitor closely</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                      <span className="font-medium text-orange-900">
                        Blood Pressure
                      </span>
                      <div className="flex items-center gap-2 text-orange-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm">Slight elevation</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "goals":
        return (
          <div className="space-y-8">
            <HealthGoalsTracker
              ref={goalsTrackerRef}
              patientId={selectedPatient}
            />

            {/* Goals-specific content */}
            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Goal Achievement Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-l-4 border-green-500 pl-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-slate-900">
                          Lower Cholesterol
                        </h4>
                        <Badge className="bg-green-100 text-green-700">
                          On Track
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Target: March 2024
                      </p>
                      <div className="mt-2">
                        <Progress value={75} className="h-2" />
                      </div>
                    </div>

                    <div className="border-l-4 border-yellow-500 pl-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-slate-900">
                          Increase Vitamin D
                        </h4>
                        <Badge className="bg-yellow-100 text-yellow-700">
                          In Progress
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Target: April 2024
                      </p>
                      <div className="mt-2">
                        <Progress value={45} className="h-2" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                    Upcoming Milestones
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div>
                        <p className="font-medium text-blue-900">
                          3-Month Blood Panel
                        </p>
                        <p className="text-sm text-blue-700">March 15, 2024</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <div>
                        <p className="font-medium text-purple-900">
                          Vitamin D Recheck
                        </p>
                        <p className="text-sm text-purple-700">April 1, 2024</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      default:
        // Default to comprehensive view
        return (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-8">
              {reportData && (
                <BiomarkerTrends
                  tests={reportData.tests}
                  timeRange={timeRange}
                />
              )}

              {recommendations.length > 0 && (
                <PersonalizedRecommendations
                  recommendations={recommendations}
                />
              )}
            </div>

            {/* Right Column - 1/3 width */}
            <div className="space-y-8">
              <HealthGoalsTracker
                ref={goalsTrackerRef}
                patientId={selectedPatient}
              />

              {/* Quick Actions */}
              <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-600" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={() => setShowFollowUpScheduler(true)}
                  >
                    <Calendar className="w-4 h-4" />
                    Schedule Follow-up
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={() => goalsTrackerRef.current?.toggleAddGoalForm()}
                  >
                    <Target className="w-4 h-4" />
                    Set Health Goals
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export PDF Report
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {showFollowUpScheduler && patientDetails && (
        <FollowUpScheduler
          patient={patientDetails}
          doctorName="Sarah Johnson"
          onClose={() => setShowFollowUpScheduler(false)}
          onSchedule={() => {
            setShowFollowUpScheduler(false);
          }}
        />
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Health Reports & Analytics
          </h1>
          <p className="text-slate-600">
            Comprehensive health insights and personalized recommendations
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={generateComprehensiveReport}
            disabled={isGenerating}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Generate Report
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Select value={selectedPatient} onValueChange={setSelectedPatient}>
          <SelectTrigger className="bg-white/60 backdrop-blur-sm border-slate-200/60">
            <SelectValue placeholder="Select patient" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PAT001">Patient 001</SelectItem>
            <SelectItem value="PAT002">Patient 002</SelectItem>
            <SelectItem value="PAT003">Patient 003</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={selectedReportType}
          onValueChange={setSelectedReportType}
        >
          <SelectTrigger className="bg-white/60 backdrop-blur-sm border-slate-200/60">
            <SelectValue placeholder="Report type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="comprehensive">Comprehensive Report</SelectItem>
            <SelectItem value="biomarkers">Biomarker Analysis</SelectItem>
            <SelectItem value="trends">Health Trends</SelectItem>
            <SelectItem value="goals">Goals & Progress</SelectItem>
          </SelectContent>
        </Select>

        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="bg-white/60 backdrop-blur-sm border-slate-200/60">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="3months">Last 3 Months</SelectItem>
            <SelectItem value="6months">Last 6 Months</SelectItem>
            <SelectItem value="1year">Last Year</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" className="gap-2">
          <Share className="w-4 h-4" />
          Share Report
        </Button>
      </div>

      {/* Health Score Dashboard */}
      {healthScore && (
        <HealthScoreCard healthScore={healthScore} isLoading={isGenerating} />
      )}

      {/* Dynamic Report Content */}
      {renderReportContent()}
    </div>
  );
}
